import {
  Button,
  Checkbox,
  Combobox,
  Form,
  Grid,
  Icon,
  SectionItem,
  Tooltip,
} from '@mairistem/composants';
import { isNewIdentifiant, request } from '@mairistem/tools';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';

import { REGIE } from '@/constants';
import { getRecus } from '@/routes';
import type { ApiResponseType, RecuType } from '@/types';

import { ModalMailRecu } from '../../Recu';

type RecuFormProps = {
  identifiant?: string | number;
  onChange?: (e: any) => void;
  titreRecuImprime?: string;
  refresh?: boolean;
  envoiRecuParMail?: boolean;
  recuDetailleProduit?: boolean;
  htmlMailRecu?: string;
};

const Recu = (props: RecuFormProps) => {
  const {
    identifiant,
    titreRecuImprime,
    envoiRecuParMail = false,
    recuDetailleProduit = false,
    htmlMailRecu,
    refresh,
    onChange,
  } = props;

  const [regieHasRecus, setRegieHasRecus] = React.useState(null);
  const [modalMailRecuOpen, setModalMailRecuOpen] = React.useState<boolean>(false);
  const refreshRef = React.useRef<boolean>(refresh);

  const handleTitreChange = React.useCallback(
    (e: unknown, { value }: { value: string } = { value: '0' }) => {
      if (onChange) {
        onChange({
          identifiant,
          titreRecuImprime: REGIE.TITRES_RECU_IMPRIME[value],
        });
      }
    },
    [identifiant, onChange],
  );

  const handleCheckboxChange = React.useCallback(
    (e: unknown, { name, checked }: { name: string; checked: boolean }) => {
      if (onChange) {
        onChange({
          identifiant,
          [name]: checked,
        });
      }
    },
    [identifiant, onChange],
  );

  const handleModalMailRecuToggle = React.useCallback(() => {
    setModalMailRecuOpen((p) => !p);
  }, []);

  const handleMailRecuSave = React.useCallback(
    (html: string) => {
      if (onChange) {
        onChange({
          identifiant,
          htmlMailRecu: html,
        });
      }

      setModalMailRecuOpen(false);
    },
    [identifiant, onChange],
  );

  const getRegieRecus = React.useCallback(() => {
    if (_.isNil(identifiant)) {
      setRegieHasRecus(null);
    } else if (isNewIdentifiant(identifiant)) {
      setRegieHasRecus(false);
    } else {
      request
        .get(
          getRecus({
            dateCreation: `after(${dayjs().year()}-01-01)`,
          }),
        )
        .then(({ meta: { total } }: ApiResponseType<RecuType>) => {
          setRegieHasRecus(total > 0);
        });
    }
  }, [identifiant]);

  // Initialise le state regieHasRecus
  // Si une régie a des reçus sur l'année civile en cours,
  // on ne peut pas changer le titre des reçus imprimés
  React.useEffect(() => {
    getRegieRecus();
  }, [getRegieRecus]);

  React.useEffect(() => {
    if (refreshRef.current && !refresh) {
      getRegieRecus();
    }

    refreshRef.current = refresh;
  }, [getRegieRecus, refresh]);

  const titreRecuImprimeOptions = _.map(REGIE.TITRES_RECU_IMPRIME, (titre, index) => ({
    key: `titre@${index}`,
    text: titre,
    value: index,
  }));

  const valueTitreRecuImprime = React.useMemo(
    () => _.findIndex(REGIE.TITRES_RECU_IMPRIME, (titre) => titre === titreRecuImprime),
    [titreRecuImprime],
  );

  return (
    <>
      <Grid stackable relaxed as={Form}>
        <Grid.Row style={{ paddingTop: 0 }}>
          <Grid.Column width={5} style={{ paddingLeft: 0 }}>
            <SectionItem title="Titre donné aux reçus imprimés" single compact>
              <Form.Field required width={10}>
                <label style={{ display: 'flex', alignItems: 'center' }}>
                  Titre donné aux reçus imprimés
                  {regieHasRecus === true && (
                    <Tooltip
                      type="warning"
                      trigger={
                        <Icon name="Info" iconSet="Lucide" size={20} style={{ marginLeft: 5 }} />
                      }
                      message={
                        <div style={{ width: 450 }}>
                          La régie a déjà des reçus sur l&apos;année civile en cours.
                          <br />
                          Il n&apos;est plus possible de modifier le titre donné aux reçus imprimés.
                        </div>
                      }
                      position="bottom center"
                    />
                  )}
                </label>
                {/* @ts-expect-error */}
                <Combobox
                  data-testid="recu-select-titre-recu"
                  menuPortalTarget={document.body}
                  disabled={regieHasRecus !== false}
                  selection
                  loading={regieHasRecus === null}
                  name="titreRecuImprime"
                  value={valueTitreRecuImprime === -1 ? 0 : valueTitreRecuImprime}
                  options={titreRecuImprimeOptions}
                  onChange={handleTitreChange}
                />
              </Form.Field>
            </SectionItem>
          </Grid.Column>
          <Grid.Column width={5}>
            <SectionItem title="Édition du reçu détaillée par produit" single compact>
              <Form.Group>
                <Checkbox
                  data-testid="recu-checkbox-detaille-produit"
                  label="Édition du reçu détaillée par produit"
                  checked={recuDetailleProduit}
                  onChange={handleCheckboxChange}
                  name="recuDetailleProduit"
                  toggle
                />
              </Form.Group>
            </SectionItem>
          </Grid.Column>
          <Grid.Column width={6} style={{ paddingRight: 0 }}>
            <SectionItem title="Envoi des reçus par mail" single compact>
              <Form.Group
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Checkbox
                  data-testid="recu-checkbox-envoi-par-mail"
                  toggle
                  label="Envoi par mail"
                  checked={envoiRecuParMail}
                  onChange={handleCheckboxChange}
                  name="envoiRecuParMail"
                />
                <Button
                  data-testid="recu-button-personnalisation-mail"
                  basic
                  disabled={!envoiRecuParMail}
                  content="Personnalisation du mail"
                  icon="setting"
                  style={{ transform: 'translateY(-25%)' }}
                  onClick={handleModalMailRecuToggle}
                />
              </Form.Group>
            </SectionItem>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {modalMailRecuOpen && (
        <ModalMailRecu
          htmlMailRecu={htmlMailRecu}
          onClose={handleModalMailRecuToggle}
          onSave={handleMailRecuSave}
        />
      )}
    </>
  );
};

export default Recu;
